// The two tabs every drawer declares and most of them never rendered.
//
// Six drawers offered a "Documents" or "Activity" tab that fell through to
// the Overview body — the drawer branches on the tabs it implements and
// returns the overview for anything else, so an unimplemented tab does not
// look broken, it looks like the overview. Recoveries showed the same panel
// under three of its four tabs.
//
// These live here rather than in each drawer because the content is
// identical everywhere: a record's attachments are a record's attachments.
// Six copies is how they drift.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_URL, C, Panel, MiniTable, date } from './kit';

const fileSize = (bytes) => {
  const n = Number(bytes);
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// ─── Documents ────────────────────────────────────────────────
//
// Two different lists that both mean "documents", and conflating them loses
// the point of each:
//
//   requiredDocuments — the CHECKLIST. What this record needs before it can
//     move on, ticked or not. The gap is the work outstanding.
//   documents — the ATTACHMENTS actually on the record.
//
// A record can have either, both or neither.
export function DocumentsTab({ record, checklistTitle = 'Required documents' }) {
  const required = record?.requiredDocuments || [];
  const attached = record?.documents || [];
  const done = required.filter(d => d.uploaded || d.completed).length;

  if (!required.length && !attached.length) {
    return (
      <Panel title='Documents'>
        <div style={{ fontSize: 12.5, color: C.faint, lineHeight: 1.55 }}>
          Nothing on file for this record yet.
        </div>
      </Panel>
    );
  }

  return (
    <>
      {required.length > 0 && (
        <Panel title={checklistTitle} action={<span style={{ fontSize: 11.5, color: C.muted }}>{done} of {required.length}</span>}>
          <MiniTable
            head={['Document', 'Status', 'Received']}
            rows={required.map(d => [
              d.label || d.key,
              (d.uploaded || d.completed)
                ? <span key='s' style={{ color: C.green }}>Received</span>
                : <span key='s' style={{ color: C.muted }}>Outstanding</span>,
              date(d.uploadedAt || d.completedAt),
            ])}
            empty='No checklist on this record.'
          />
        </Panel>
      )}

      {attached.length > 0 && (
        <Panel title='Attachments'>
          <MiniTable
            head={['File', 'Kind', 'Size', 'Uploaded']}
            rows={attached.map(a => [
              a.label || a.filename || '—',
              a.kind || '—',
              fileSize(a.sizeBytes),
              date(a.uploadedAt),
            ])}
            empty='Nothing attached.'
          />
        </Panel>
      )}
    </>
  );
}

// ─── Activity ─────────────────────────────────────────────────
//
// The audit trail, read from the platform's own Log rows rather than a
// per-record history array — the reinsurance models do not carry one, and
// adding one would be a second copy of something already kept for every
// mutating request.
//
// `resource` names the record type for the endpoint's allow-list; the
// backend proves the record is the caller's before returning any row.
// `limit` and `compact` so the same component can be a full tab or a short
// tail inside an Overview panel, rather than a second near-identical one.
export function ActivityTab({ resource, id, limit, compact = false }) {
  const [state, setState] = useState({ loading: true, entries: [], error: null });

  useEffect(() => {
    let live = true;
    if (!resource || !id) { setState({ loading: false, entries: [], error: null }); return undefined; }
    setState({ loading: true, entries: [], error: null });
    axios.get(`${BASE_URL}/reinsurance/activity/${resource}/${id}`, { withCredentials: true })
      .then((resp) => {
        if (!live) return;
        setState({ loading: false, entries: resp?.data?.data?.entries || [], error: null });
      })
      .catch((err) => {
        if (!live) return;
        setState({
          loading: false,
          entries: [],
          error: err?.response?.data?.message || 'Could not load the activity for this record.',
        });
      });
    // The drawer stays mounted while the selected row changes, so a stale
    // response must not land on a different record.
    return () => { live = false; };
  }, [resource, id]);

  if (state.loading) {
    return <Panel title='Activity'><div style={{ fontSize: 12.5, color: C.faint }}>Loading…</div></Panel>;
  }
  if (state.error) {
    return <Panel title='Activity' tone='warn'><div style={{ fontSize: 12.5, color: C.ink }}>{state.error}</div></Panel>;
  }

  const shown = limit ? state.entries.slice(0, limit) : state.entries;
  const table = (
    <MiniTable
      head={compact ? ['When', 'Who', 'Action'] : ['When', 'Who', 'Action', 'Detail']}
      rows={shown.map(e => (compact
        ? [date(e.time), e.user, e.action]
        : [date(e.time), e.user, e.action, e.detail || '—']))}
      empty='No recorded activity. Changes made from here will appear in this list.'
    />
  );
  // Compact means "you are already inside somebody else's Panel" — wrapping
  // again would draw a card in a card.
  return compact ? table : <Panel title='Activity'>{table}</Panel>;
}

const tabs = { DocumentsTab, ActivityTab };
export default tabs;
